import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsFormTextInputField,
  HdsSeparator,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FormInputsRegionSelect,
  Disclosure,
  StickyFooter,
  FlexGrid,
  Typography,
  LimitBanner,
  WithErrors,
} from 'core/components';
import { boxMargin } from 'core/modifiers';
import { NETWORK_SUBMITTED } from 'core/utils/consts/analytics-events/platform';
import {
  RegionProviders,
  DEFAULT_HVN_PROVIDER_REGION,
  DEFAULT_SELECTED_PROVIDER_REGION,
} from 'core/utils/regions';
import { TYPE_NETWORK } from 'common/utils/cloud-resource-types';
import ProviderCards from 'networks-common/components/networks/form-inputs/provider-cards';
import CidrRecommendations from 'networks-common/components/networks/cidr-recommendations';
import UpNext from 'networks-common/components/networks/cards/up-next';

/**
 *
 * `model` `project` and `networks` attributes
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnsCreate` renders the HashiCorp Virtual Networks Create page.
 *
 *
 * ```
 * <Page::Hvns::Create
 *   @model={{@model}}
 * />
 * ```
 *
 * @class PageHvnsCreate
 *
 */
export default class PageHvnsCreate extends Component {
  @service analytics;
  @service api;
  @service abilities;
  @service flashMessages;
  @service intl;

  @service router;

  @tracked errors;
  @tracked id = 'hvn';
  @tracked cidrBlock = '172.25.16.0/20';
  @tracked provider = DEFAULT_HVN_PROVIDER_REGION.provider;
  @tracked region = DEFAULT_HVN_PROVIDER_REGION.region;

  get resourceType() {
    return TYPE_NETWORK;
  }

  get supportedRegions() {
    // HVN Create route model has awsRegions and azureRegions
    return this.args.model[`${RegionProviders[this.provider]}Regions`] || [];
  }
  onProviderChange = ({ target }) => {
    let { value } = target;
    this.provider = value;
    this.region = DEFAULT_SELECTED_PROVIDER_REGION[value];
  };

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    let {
      parent: { id: orgId },
      id: projectId,
    } = this.args.model.project;
    let { id, cidrBlock, provider, region } = this;
    let resp;
    let payload = {
      network: {
        id,
        cidrBlock,
        location: {
          region: {
            provider,
            region,
          },
        },
      },
    };

    this.analytics.trackEvent(NETWORK_SUBMITTED, payload);

    // TODO: [TCE-2007] Remove HVN activation A/B test analytics instrumentation.
    const abTestVariation = variation('hcp-ui-ab-test-hvn-activation-create');

    // TODO: [TCE-2007] Remove HVN activation A/B test analytics instrumentation.
    this.analytics.trackEvent(
      'external_ui_hvn_activation_create_success_form_submitted',
      { ...payload, abTestVariation }
    );

    resp = await this.api.network.create(orgId, projectId, payload);
    this.flashMessages.success(
      this.intl.t('components.page.hvns.create.success.title')
    );

    return this.router.transitionTo(
      'cloud.orgs.detail.projects.detail.hvns.detail',
      orgId,
      projectId,
      resp.network.id
    );
  });

  <template>
    <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t "components.page.hvns.create.title"}}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          <LimitBanner
            @padding="0 0 md 0"
            @variant={{this.resourceType}}
            @count={{@model.networks.length}}
            as |A|
          >
            <A.LinkStandalone
              @icon="arrow-right"
              @iconPosition="trailing"
              @route="cloud.orgs.detail.projects.detail.hvns.list"
              @text={{t "components.limit-banner.create-cta.network"}}
            />
          </LimitBanner>
          {{#if this.save.last.error}}
            <FormError />
          {{/if}}
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="8" @md="8" @lg="6">
              <form
                data-test-form
                class="hcp-hds-form"
                {{on "submit" this.save.perform}}
              >
                <Typography
                  @variant="body1"
                  class="hds-typography-body-200 hds-font-weight-regular hds-foreground-faint"
                  {{boxMargin "0 0 md 0"}}
                >
                  {{t "components.page.hvns.create.description"}}
                </Typography>
                <section {{boxMargin "0 0 2xl 0"}}>
                  <FieldError @field="id" @hdsDelegatedError={{true}} as |err|>
                    <HdsFormTextInputField
                      @extraAriaDescribedBy="id_error"
                      @id="id"
                      @isInvalid={{err}}
                      @type="text"
                      @value={{this.id}}
                      name="id"
                      data-test-network-name
                      {{on "input" (pick "target.value" (set this "id"))}}
                      as |F|
                    >
                      <F.Label>
                        {{t
                          "components.page.hvns.create.form.label.network-name"
                        }}
                      </F.Label>
                      <F.HelperText>
                        {{t
                          "components.page.hvns.create.form.label.network-name-help"
                        }}
                      </F.HelperText>
                      {{#if err}}
                        <F.Error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                  </FieldError>
                </section>
                {{#if (variation "hcp-azure-integration")}}
                  <section {{boxMargin "0 0 2xl 0"}}>
                    <header>
                      <Typography
                        @variant="h2"
                        class="hds-typography-display-300 hds-font-weight-bold"
                        {{boxMargin "0 0 sm 0"}}
                      >
                        {{t "components.page.hvns.create.form.header.provider"}}
                      </Typography>
                    </header>
                    <ProviderCards
                      @onChange={{this.onProviderChange}}
                      @selectedProvider={{this.provider}}
                      @headerType="h2"
                    />
                  </section>
                {{/if}}
                <section {{boxMargin "0 0 2xl 0"}}>
                  <FormInputsRegionSelect
                    @provider={{this.provider}}
                    @regions={{this.supportedRegions}}
                    @selectedRegion={{this.region}}
                    @onChange={{pick "target.value" (set this "region")}}
                    data-test-region
                    {{boxMargin "0 0 2xs 0"}}
                  >
                    <:label>
                      <Typography
                        @variant="h2"
                        class="hds-typography-display-300 hds-font-weight-semibold"
                      >
                        {{t
                          "components.page.hvns.create.form.header.region-selection"
                        }}
                      </Typography>
                    </:label>
                    <:helper-text>
                      {{t
                        "components.page.hvns.create.form.label.region-help-cluster"
                        provider=this.provider
                        htmlSafe=true
                      }}
                    </:helper-text>
                  </FormInputsRegionSelect>
                </section>
                <section {{boxMargin "0 0 2xl 0"}}>
                  <header
                    aria-label={{t
                      "components.page.hvns.create.form.header.network-config"
                    }}
                  >
                    <Typography
                      @variant="h2"
                      class="hds-typography-display-300 hds-font-weight-semibold"
                      {{boxMargin "0 0 md 0"}}
                    >
                      {{t
                        "components.page.hvns.create.form.header.network-config"
                      }}
                    </Typography>
                    <Typography
                      @variant="body1"
                      class="hds-typography-body-200 hds-font-weight-regular hds-foreground-faint"
                      {{boxMargin "0 0 md 0"}}
                    >
                      {{t
                        "components.page.hvns.create.form.header.network-config-description"
                        provider=this.provider
                        htmlSafe=true
                      }}
                    </Typography>
                  </header>
                  <FieldError
                    @field="network.cidr_block"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @extraAriaDescribedBy="cidrHelp"
                      @id="cidrBlock"
                      @isInvalid={{err}}
                      @type="text"
                      @value={{this.cidrBlock}}
                      name="cidrBlock"
                      {{on
                        "input"
                        (pick "target.value" (set this "cidrBlock"))
                      }}
                      as |F|
                    >
                      <F.Label>
                        {{t
                          "components.page.hvns.create.form.label.cidr-block"
                        }}
                      </F.Label>
                      <F.HelperText>
                        {{t
                          "components.page.hvns.create.form.label.cidr-block-help"
                        }}
                      </F.HelperText>
                    </HdsFormTextInputField>
                  </FieldError>
                  <Disclosure
                    @label={{t
                      "components.page.hvns.create.form.cidr-recommendations.label"
                    }}
                    @openLabel={{t
                      "components.page.hvns.create.form.cidr-recommendations.open-label"
                    }}
                  >
                    <CidrRecommendations @provider={{this.provider}} />
                  </Disclosure>
                </section>
                <HdsSeparator />
                <UpNext
                  @resourceType={{this.resourceType}}
                  @provider={{this.provider}}
                />
                <StickyFooter>
                  <HdsButtonSet>
                    {{#if (can "create network" @model.networks)}}
                      <HdsButton
                        data-test-submit
                        @text={{t "components.page.hvns.create.action"}}
                        type="submit"
                        disabled={{this.save.isRunning}}
                      />
                    {{/if}}
                    <HdsButton
                      @color="secondary"
                      @iconPosition="leading"
                      @route="cloud.orgs.detail.projects.detail.hvns.list"
                      @text={{t "components.form.cancel"}}
                      data-test-cancel
                    />
                  </HdsButtonSet>
                </StickyFooter>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
